import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DemandDataService } from '../../demand/demand-data.service';
import { PurchaseRequisitionDataService } from '../purchase-requisition-data.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SubscriptionHelper } from '@lib/util';
import { takeUntil } from 'rxjs/operators';
import { PurchaseRequisition } from '../types/PurchaseRequisition';
import { PurchaseRequisitionHistoryEntry } from '../types/PurchaseRequisitionHistoryEntry';
import { DemandContentComponent } from '../../demand/display/content/demand-content.component';
import { PurchaseRequisitionStatusEnum } from '../types/PurchaseRequistionStatusEnum';
import { NotificationMessageLevel, NotificationService, View } from '@lib/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { PurchaseRequisitionTask } from '../../workflow/types/PurchaseRequisitionTask';
import { DemandSupplier } from '../../demand/types/IDemandSupplierContact';
import { DemandSelectableCurrencies } from '../../demand/types/IDemandSelectableCurrencies';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';

const TEXTS = {
    accessDeniedError: marker('Sie haben keine Berechtigung für diese Bedarfsmeldung.'),
    actionDeniedError: marker('Sie haben keine Berechtigung für diese Aktion.'),
    notFoundError: marker('Die Bedarfsmeldung wurde nicht gefunden.'),
    loadError: marker('Bedarfsmeldung konnte nicht geladen werden.'),
    withdrawError: marker('Bedarfsmeldung konnte nicht zurückgezogen werden.'),
};

@Component({
    selector: 'purchase-requisition-detail',
    templateUrl: './purchase-requisition-detail.component.html',
    styleUrls: ['./purchase-requisition-detail.component.scss'],
    standalone: false
})
export class PurchaseRequisitionDetailComponent extends SubscriptionHelper implements OnInit {
    private readonly demandDataService = inject(DemandDataService);
    private readonly purchaseRequisitionDataService = inject(PurchaseRequisitionDataService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly notificationService = inject(NotificationService);
    private readonly breadcrumbService = inject(BreadcrumbService);

    @ViewChild('demandContentComponent') private readonly demandContentComponent?: DemandContentComponent;
    @Input() public purchaseRequisition?: PurchaseRequisition;
    @Input() editingWithDialog = false;
    @Input() supplierRequired = false;
    @Input() supplierChangeAllowed = true;
    @Input() validUntilRequired = false;

    /**
     * If Provided we are in a task with this purchase-requisition as the subject
     */
    @Input() task?: PurchaseRequisitionTask;

    @Output() supplierChanged: EventEmitter<DemandSupplier[]> = new EventEmitter<DemandSupplier[]>();
    /** Re-emitted currency acceptance of the demand (EPROC-11425), see DemandContentComponent. */
    @Output() currencyAcceptanceChanged = new EventEmitter<DemandSelectableCurrencies | undefined>();
    @Output() validationStatusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() initialized = new EventEmitter<void>();

    public history?: PurchaseRequisitionHistoryEntry[];
    public isValid = false;

    ngOnInit(): void {
        this.getPurchaseRequisition();
    }

    public setValid(isValid: boolean): void {
        if (this.isValid !== isValid) {
            this.isValid = isValid;
            this.validationStatusChanged.emit(isValid);
        }
    }

    public withdraw(): void {
        if (
            this.purchaseRequisition &&
            PurchaseRequisitionStatusEnum.PENDING === this.purchaseRequisition.status.value
        ) {
            this.purchaseRequisitionDataService.withdraw(this.purchaseRequisition.id).subscribe({
                next: () => {
                    const successMessage = marker('Bedarfsmeldung zurückgezogen.');
                    this.notificationService.showNotification(successMessage, NotificationMessageLevel.SUCCESS);
                    void this.router.navigate([View.CLIENT, 'rfq', 'purchase-requisition', 'list']);
                },
                error: (error: unknown) => {
                    this.notificationService.showNotification(
                        this.resolveActionErrorMessage(error, TEXTS.withdrawError),
                        NotificationMessageLevel.ERROR,
                    );
                },
            });
        }
    }

    private getPurchaseRequisition(): void {
        if (!this.purchaseRequisition) {
            this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe((params: Params) => {
                const id = params['id'];
                if (id) {
                    this.purchaseRequisitionDataService
                        .findOne(id)
                        .subscribe({
                            next: (purchaseRequisition: PurchaseRequisition) => {
                                this.purchaseRequisition = purchaseRequisition;
                                this.breadcrumbService.set(
                                    BC.PURCHASE_REQUISITION.LIST,
                                    BC.PURCHASE_REQUISITION.DETAIL(
                                        purchaseRequisition.id,
                                        purchaseRequisition.demand.title ?? purchaseRequisition.number,
                                    ),
                                );
                                this.getHistory(purchaseRequisition.id);
                            },
                            error: (error: unknown) => {
                                this.notificationService.showNotification(
                                    this.resolveReadErrorMessage(error),
                                    NotificationMessageLevel.ERROR,
                                );
                                void this.router.navigate([View.CLIENT, 'rfq', 'purchase-requisition', 'list']);
                            },
                        });
                }
            });
        } else {
            this.getHistory(this.purchaseRequisition.id);
        }
    }

    private getHistory(purchaseRequisitionId: string): void {
        this.purchaseRequisitionDataService.getHistory(purchaseRequisitionId).subscribe({
            next: (history) => (this.history = history),
            // An empty history still counts as loaded, otherwise the requisition view would stay on its skeleton.
            error: () => (this.history = []),
        });
    }

    private resolveReadErrorMessage(error: unknown): string {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 403) {
                return TEXTS.accessDeniedError;
            }
            if (error.status === 404) {
                return TEXTS.notFoundError;
            }
        }

        return TEXTS.loadError;
    }

    private resolveActionErrorMessage(error: unknown, fallbackMessage: string): string {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 403) {
                return TEXTS.actionDeniedError;
            }
            if (error.status === 404) {
                return TEXTS.notFoundError;
            }
        }

        return fallbackMessage;
    }
}
